import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { queryClient } from './lib/queryClient';
import { SessionProvider } from './lib/session';
import { AssistenteProvider } from './lib/assistente';
import { SincronizarAssistente } from './components/SincronizarAssistente';
import { ToastProvider } from './components/ui';
import { router } from './router';
import { carregarIdioma, idiomaDaTela } from './lib/i18n';
import { validateHelpPoints } from './help/registry';
import './styles.css';

// Um link de ajuda quebrado promete explicação e entrega tela vazia. A checagem
// roda apenas em desenvolvimento — em produção não custa nada.
if (import.meta.env.DEV) {
  const problems = validateHelpPoints();
  if (problems.length > 0) {
    console.error('[ajuda] pontos de ajuda inválidos:\n' + problems.join('\n'));
  }
}

const container = document.getElementById('root');
if (container === null) {
  throw new Error('Elemento #root não encontrado.');
}

/*
 * O DICIONÁRIO DO IDIOMA ATIVO CHEGA ANTES DO PRIMEIRO RENDER.
 *
 * O inglês passou a ser carregado sob demanda (ver `lib/i18n.ts`). Sem esta
 * espera, quem tem o navegador em inglês veria a tela nascer em português e
 * trocar de língua um instante depois — pior do que a espera que a divisão
 * economizou. Em português a promessa já está resolvida e nada espera.
 */
function iniciar(): void {
  createRoot(container!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <SessionProvider>
            {/* Acima do roteador de propósito: a conversa precisa sobreviver à
                navegação. O painel inicial e o acionador global são dois pontos de
                entrada para o MESMO estado — pedir algo no painel, ir para
                Empresas e encontrar o assistente sem memória seria pior do que não
                ter o atalho. */}
            <AssistenteProvider>
              {/* Troca de pessoa ou de organização zera a conversa (U2). */}
              <SincronizarAssistente />
              <RouterProvider router={router} />
            </AssistenteProvider>
          </SessionProvider>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

/*
 * `void` em vez de `await` no topo do módulo: o alvo do build é ES2020, e
 * `top-level await` exige um formato de módulo que o navegador mais antigo da
 * lista não entende. A promessa resolve de imediato em português (o dicionário
 * já está em memória) e, em inglês, espera o único arquivo que falta.
 */
void carregarIdioma(idiomaDaTela()).then(iniciar, iniciar);
